import { Product, ProductDeprecatedEnum } from "@/types/product";
import { App, Button, Flex, Input, Popover, Select, TableColumnsType, Tag, Tooltip } from "antd";
import { FC, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { productList, deprecated, replacementId, ProductListObjParams, ProductListBody } from "@/apis/product";
import { useTranslation } from "react-i18next";
import AddOrEdit from "./AddOrEdit";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, ITableLayout, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { CustomTabPaneContext } from "@/components/TabPane";
import { useSelector } from "react-redux";
import { RootState } from "@/store/index";
import { User, UserPermissionEnum } from "@/types/user";
import { Org } from "@/types/org";
import { getUserAvatarTip, getUserName, hasPermission } from "@/utils/user";

import iconImage from "@/assets/images/fileIcon/image.svg";
import iconFolder from "@/assets/images/fileIcon/folder.svg";

import SText from "@/styles/SText";
import { flushSync } from "react-dom";
import _ from "lodash";
import { useProductDeprected } from "../hooks/useProductDeprected";
import ProductSelect from "@/components/Selector/ProductSelect";
import { CommonListParams } from "@/types/common";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import WMSAvatar from "@/styles/WMSAvatar";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";

interface ProductListProps {
  hiddenColumns?: Array<keyof Product>;
  categoryId?: string;
  selectType?: 'multi' | 'select'
}

interface ProductFilters {
  keyword: string;
}

const INITIAL_PRODUCT_FILTERS: ProductFilters = {
  keyword: "",
};

const ProductList: FC<ProductListProps> = ({
  hiddenColumns = [],
  categoryId,
  selectType
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { onOpen } = useContext(CustomTabPaneContext);
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);
  const curOrg: Org = useSelector((state: RootState) => state.orgReducer.curOrg);

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const exportAbortControllerRef = useRef<AbortController | null>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<ProductFilters>(INITIAL_PRODUCT_FILTERS);
  const { keyword } = draftFilters;
  const [isOpenAdd, setIsOpenAdd] = useState<boolean>(false);
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<Product | null>(null);
  const [selectProIds, setSelectProIds] = useState<Array<string>>([]);
  const [selectPros, setSelectPros] = useState<Array<Product>>([]);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const { getDeprecatedLabel } = useProductDeprected();

  const [list, total, loading, { fetchList, addItem, updateItem, rmItem }] = useDataList<Product, [ProductListObjParams]>({ fetchApi: productList });

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      exportAbortControllerRef.current?.abort();
    };
  }, [categoryId]);

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_PRODUCT_FILTERS);
    tableRef.current?.resetTablePage();
    fetchProducts(0, nextFilters);
    // 独立商品列表首次加载，以及类目内商品列表切换类目时，统一重置筛选和页码。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, replaceFilters]);

  const getContent: (item: Product | null) => ReactNode = item => {
    if (!!!item) return null
    return (
      <div>
        <div>
          {t("product.code")}: {item.code}
        </div>
        <div>
          {t("product.name")}: {item.name}
        </div>
        <div>
          {t("product.brand1")}: {item.model?.toLocaleUpperCase()}
        </div>
        <div>
          {t("product.brand2")}: {item.t1?.toLocaleUpperCase()}
        </div>
        <div>
          {t("product.uom")}: {item?.uom?.toLocaleUpperCase()}
        </div>
        <div>
          {t("product.cbm")}: {item.cbm}
        </div>
      </div>
    );
  };

  const columns: TableColumnsType<Product> = [
    {
      width: 100,
      title: t("product.code"),
      dataIndex: "productCode",
      hidden: hiddenColumns.includes('code'),
      sorter: (a, b) => {
        const valA = a?.code ?? "";
        const valB = b?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.code} />
    },
    {
      width: 120,
      title: t("product.name"),
      dataIndex: "productName",
      hidden: hiddenColumns.includes('name'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.name} />
    },
    {
      width: 140,
      title: t("product.brand1"),
      dataIndex: "productModel1",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.model} />
    },
    {
      width: 140,
      title: t("product.brand2"),
      dataIndex: "productModel2",
      hidden: hiddenColumns.includes('t1'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.t1 ?? ''} />
    },
    {
      width: 100,
      title: t("product.uom"),
      dataIndex: "productUom",
      hidden: hiddenColumns.includes('t1'),
      render: (_, product) => <SText text={product?.uom} />,
    },
    {
      width: 100,
      title: `${t('category.hsCode')}`,
      dataIndex: "hsCode",
      hidden: hiddenColumns.includes('hsCode'),
      sorter: (a, b) => {
        const valA = a?.hsCode ?? "";
        const valB = b?.hsCode ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, product) => <SText text={product?.hsCode || ''} />,
    },
    {
      width: 100,
      title: `cbm(${t('product.cbm')})`,
      dataIndex: "productCbm",
      hidden: hiddenColumns.includes('cbm'),
      render: (_, product) => <SText text={product?.cbm || ''} />,
    },
    {
      width: 100,
      title: `Process(${t('product.process')})`,
      dataIndex: "productProcess",
      hidden: hiddenColumns.includes('process'),
      render: (_, product) => <SText text={product?.process || ''} />,
    },
    {
      width: 100,
      dataIndex: "category",
      title: t("product.category"),
      hidden: hiddenColumns.includes('category'),
      render: (_, { category }) => <SText text={category?.name || ''} />,
    },
    {
      width: 100,
      dataIndex: "preReady",
      title: t("product.preReady"),
      hidden: hiddenColumns.includes('category'),
      render: (_, product) => <SText text={product?.preReady === 1 ? t("product.isTrue") : t("product.isFalse")} />,
    },
    {
      title: t('common.desc'),
      dataIndex: "productDesc",
      width: 100,
      hidden: hiddenColumns.includes('desc'),
      render: (_, { desc }) => <SText text={desc} />
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      width: 80,
      dataIndex: "gallery",
      title: t("product.gallery"),
      hidden: hiddenColumns.includes('picDocId'),
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconImage}
            alt=""
            onClick={() => {
              if (item.picDocId) {
                onOpen("img", item);
              } else {
                notification.error({ placement: 'top', message: t("product.dataError") });
              }
            }}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      width: 80,
      dataIndex: "folder",
      title: t("product.folder"),
      fixed: "right",
      hidden: hiddenColumns.includes('docId'),
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => {
              if (item.docId) {
                onOpen("doc", item);
              } else {
                notification.error({ placement: 'top', message: t("product.dataError") });
              }
            }}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      width: 100,
      dataIndex: "deprecated",
      title: t("product.isDeprecated"),
      hidden: hiddenColumns.includes('deprecated'),
      fixed: "right",
      render: (_, product) => {
        const canEdit = hasPermission(curUser, [categoryId ? UserPermissionEnum.PRODUCT_CATEGORY_ADD_OR_EDIT : UserPermissionEnum.PRODUCT_ADD]);
        const isLoading = updatingIds.has(product.id);
        if (!canEdit) {
          const label = getDeprecatedLabel(product);
          return label ? <Tag color={label.color}>{label.text}</Tag> : '-';
        }
        return (
          <Select
            style={{ width: '100%' }}
            value={product?.deprecated ?? null}
            loading={isLoading}
            disabled={isLoading}
            onChange={(value) => {
              setUpdatingIds(prev => new Set(prev).add(product.id));
              deprecated(product.id, value).then((res) => {
                updateItem(res);
                notification.success({ placement: 'top', message: t('product.modifySuccess') });
              }).catch((err) => {
                notification.error({ placement: 'top', message: t('product.modifyFailed'), description: err });
              }).finally(() => {
                setUpdatingIds(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(product.id);
                  return newSet;
                });
              });
            }}
            options={[
              { label: t('product.deprecated'), value: ProductDeprecatedEnum.DEPRECATED },
              { label: t('product.normalStatus'), value: ProductDeprecatedEnum.UN_DEPRECATED },
              { label: t('product.notSet'), value: null },
            ]}
          />
        );
      },
    },
    {
      width: 250,
      dataIndex: "replacement",
      title: t("bom.itemReq"),
      hidden: hiddenColumns.includes('replacement'),
      fixed: "right",
      render: (_, product) => {
        const canEdit = hasPermission(curUser, [categoryId ? UserPermissionEnum.PRODUCT_CATEGORY_ADD_OR_EDIT : UserPermissionEnum.PRODUCT_ADD]);
        const isLoading = updatingIds.has(product.id);
        if (!canEdit) {
          return <Popover showArrow={false} content={() => getContent(product.replacement)} >
            <Tag
              color="green"
            >
              {product.replacement?.code || '-'}
            </Tag>
          </Popover>;
        }
        return (
          <ProductSelect
            value={product?.replacementId ?? undefined}
            defaultList={product?.replacement ? [product.replacement] : undefined}
            disabledProducts={product ? [product.id] : undefined}
            loading={isLoading}
            disabled={isLoading}
            getSelectPro={(data) => {
              if (data) {
                setUpdatingIds(prev => new Set(prev).add(product.id));
                replacementId(product.id, data.id).then((res) => {
                  updateItem({
                    ...res,
                    replacement: data,
                    replacementId: data.id
                  });
                  notification.success({ placement: 'top', message: t('product.modifySuccess') });
                }).catch((err) => {
                  notification.error({ placement: 'top', message: t('product.modifyFailed'), description: err });
                }).finally(() => {
                  setUpdatingIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(product.id);
                    return newSet;
                  });
                });

              }
            }}
          />
        );
      },
    },
    {
      width: 160,
      dataIndex: "Action",
      title: t("common.opt"),
      fixed: "right",
      hidden: hiddenColumns.includes('id'),
      render: (_, item) => (
        <Flex gap={4}>
          {hasPermission(curUser, [categoryId ? UserPermissionEnum.PRODUCT_CATEGORY_ADD_OR_EDIT : UserPermissionEnum.PRODUCT_ADD]) ? (
            <Button
              type="link"
              onClick={() => {
                setIsOpenEdit(true);
                setEditRow(item);
              }}
            >
              {t("product.modify")}
            </Button>
          ) : null}
          <Button onClick={() => onOpen("store", item)} type='link'
          >{t("product.storeType")}</Button>
        </Flex>
      )
    },
  ];

  const fetchProducts = (offset: number, filters: ProductFilters) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    const normalizedKeyword = filters.keyword.trim();
    let params: CommonListParams<ProductListBody> = {
      body: { keyword: normalizedKeyword },
      pagination: { offset, take },
    }
    if (normalizedKeyword.includes(' ')) {
      params.body = undefined
      params.strs = normalizedKeyword.split(' ').filter(Boolean)
    }

    fetchList({
      categoryId, params: {
        ...params,
        controller, // ✅ 传递 controller 用于取消请求
      }
    }).catch((err) => {
      // ✅ 忽略取消的请求
      if (err?.name === 'AbortError') {
        return;
      }
    });
  };

  const exportColumns: { key: keyof Product; label: string }[] = [
    { key: 'code', label: t('product.code') },
    { key: 'name', label: t('product.name') },
    { key: 'model', label: t('product.brand1') },
    { key: 't1', label: t('product.brand2') },
    { key: 'uom', label: t('product.uom') },
    { key: 'hsCode', label: t('category.hsCode') },
    { key: 'cbm', label: `cbm(${t('product.cbm')})` },
    { key: 'process', label: `Process(${t('product.process')})` },
    { key: 'category', label: t('product.category') },
    { key: 'preReady', label: t('product.preReady') },
    { key: 'desc', label: t('common.desc') },
    { key: 'creator', label: t('common.creator') },
    { key: 'deprecated', label: t('product.isDeprecated') },
    { key: 'replacement', label: t('bom.itemReq') },
  ];
  const visibleExportColumns = exportColumns.filter(column => !hiddenColumns.includes(column.key));
  const exportDataMapper = (product: Product) => ({
    [t('product.code')]: product.code ?? '',
    [t('product.name')]: product.name ?? '',
    [t('product.brand1')]: product.model ?? '',
    [t('product.brand2')]: product.t1 ?? '',
    [t('product.uom')]: product.uom ?? '',
    [t('category.hsCode')]: product.hsCode ?? '',
    [`cbm(${t('product.cbm')})`]: product.cbm ?? '',
    [`Process(${t('product.process')})`]: product.process ?? '',
    [t('product.category')]: product.category?.name ?? '',
    [t('product.preReady')]: product.preReady === 1 ? t('product.isTrue') : t('product.isFalse'),
    [t('common.desc')]: product.desc ?? '',
    [t('common.creator')]: product.creator ? getUserName(product.creator) : '',
    [t('product.isDeprecated')]: getDeprecatedLabel(product)?.text ?? '',
    [t('bom.itemReq')]: [product.replacement?.code, product.replacement?.name].filter(Boolean).join('/'),
  });

  const handleExportAll = async (): Promise<Product[]> => {
    const controller = new AbortController();
    const take = 100;
    const products: Product[] = [];
    const normalizedKeyword = appliedFilters.keyword.trim();
    const query: CommonListParams<ProductListBody> = normalizedKeyword.includes(' ')
      ? { strs: normalizedKeyword.split(' ').filter(Boolean) }
      : { body: { keyword: normalizedKeyword } };
    let offset = 0;
    let totalSize: number | undefined;

    exportAbortControllerRef.current = controller;
    setExportLoading(true);
    setExportProgress(0);
    try {
      while (true) {
        if (controller.signal.aborted) throw new DOMException('Request canceled', 'AbortError');
        const response = await productList({
          categoryId,
          params: { ...query, pagination: { offset, take }, controller },
        });
        if (controller.signal.aborted) throw new DOMException('Request canceled', 'AbortError');

        totalSize = response.pagination?.size ?? totalSize;
        if (response.body.length === 0) {
          // 有剩余记录却返回空页时，不能将不完整的数据当作全部导出。
          if (totalSize !== undefined && offset < totalSize) return Promise.reject(10013);
          break;
        }

        products.push(...response.body);
        const pageSize = response.pagination?.take || take;
        offset += pageSize;
        if (totalSize) setExportProgress(Math.min(Math.round(offset / totalSize * 100), 99));

        if (totalSize !== undefined ? offset >= totalSize : response.body.length < pageSize) break;
      }

      setExportProgress(100);
      return products;
    } finally {
      exportAbortControllerRef.current = null;
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetchProducts(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_PRODUCT_FILTERS);
    tableRef.current?.resetTablePage();
    fetchProducts(0, nextFilters);
  };

  const headerChildren = (
    <Flex justify="space-between" align="center" style={{ width: "100%" }}>
      <Flex gap={10}>
        {hasPermission(curUser, [categoryId ? UserPermissionEnum.PRODUCT_CATEGORY_ADD_OR_EDIT : UserPermissionEnum.PRODUCT_ADD]) && (
          <Button type="primary" onClick={() => setIsOpenAdd(true)}>
            {t("category.addProduct")}
          </Button>
        )}
        <ExportExcelButton<Product>
          list={list}
          columns={visibleExportColumns}
          defaultSelectedColumns={visibleExportColumns.map(column => column.key)}
          dataMapper={exportDataMapper}
          fileName="products.xlsx"
          disabled={loading || exportLoading || list.length === 0}
        />
        <ExportExcelButton<Product>
          isAll
          loadData={handleExportAll}
          columns={visibleExportColumns}
          defaultSelectedColumns={visibleExportColumns.map(column => column.key)}
          dataMapper={exportDataMapper}
          fileName="products_all.xlsx"
          loading={exportLoading}
          disabled={loading || exportLoading || list.length === 0}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <Tooltip title={t("warehouse.searchMultiCodePlaceholder")}>
              <Input
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
                placeholder={t("warehouse.searchMultiCodePlaceholder")}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const getTableLayout = () => {
    let props: ITableLayout = {
      loading,
      dataSource: list,
      columns: columns as ColumnsType<AnyObject>,
      headerChildren,
      tableLayoutRef: tableRef,
      total,
      handleChange: (offset: number) => fetchProducts(offset, appliedFilters),
      scrollX: 1600,
    }

    if (selectType === 'multi') {
      props = {
        ...props,
        rowKey: 'id',
        rowSelection: {
          type: "checkbox",
          selectedRowKeys: selectProIds,
          onChange: (val, n) => {
            flushSync(() => {
              setSelectProIds(() =>
                Array.from(new Set(val as Array<string>))
              );
              setSelectPros(() => _.uniqBy(n as Array<Product>, "id"));
            });
          },
          getCheckboxProps: (record) => ({
            name: record.id,
            // disabled: !!values.find((item) => item?.id === record.id),
          }),
        }
      }
    }

    const TableEle = <STableLayout {...props} />
    return TableEle
  }

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      {getTableLayout()}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {(isOpenAdd || isOpenEdit) && (
        <AddOrEdit
          isAdd={isOpenAdd}
          categoryId={categoryId}
          openStatus={isOpenAdd || isOpenEdit}
          formData={isOpenAdd ? null : editRow}
          onOk={data => {
            if (isOpenAdd && ((categoryId && (categoryId === data.categoryId)) || !categoryId)) {
              addItem(data)
            } else {
              if (categoryId && categoryId !== data.categoryId) {
                rmItem(data.id)
              } else {
                updateItem(data)
              }
            }
          }}
          onCancel={() => {
            if (isOpenAdd) {
              setIsOpenAdd(false);
            } else {
              setIsOpenEdit(false);
              setEditRow(null);
            }
          }}
        />
      )}
    </Flex>
  );
};

export default ProductList;
